import Vue from "vue";
import VueRouter from "vue-router";
import Empty from "@/views/EmptyView.vue";
import VueGtag from "vue-gtag";
import store from "@/store";

Vue.use(VueRouter);

const accessControlFunctions = [
  "community.sa.tsm.read",
  "community.sa.tsm.write",
  "community.sa.tsm.device",
  "community.sa.tsm.r3",
  "community.sa.tsm.audit",
  "community.sa.tsm.admin",
  "community.sa.tsm.breakglass",
];

const haIntercomFunctions = [
  "ha.intercom.device.read",
  "ha.intercom.device.manage",
  "ha.intercom.sip.read",
  "ha.intercom.sip.manage",
  "ha.intercom.registration.read",
  "ha.intercom.registration.manage",
  "ha.intercom.alarm.read",
  "ha.intercom.alarm.handle",
  "ha.intercom.message.read",
  "ha.intercom.message.send",
  "ha.intercom.notification.manage",
  "ha.intercom.diagnostics.read",
  "ha.intercom.elevator.call",
];

const haSettingsFunctions = ["ha.intercom.notification.manage", "ha.intercom.diagnostics.read"];

const cloudIntercomReadFunctions = ["cloudIntercom.read"];

// 門禁系統預設落點：進門先看現況（監控總覽），而非最不該動的系統設定。
// 依使用者實際權限挑「進得去（能通過子路由 guard）」的第一頁，避免落到會被擋回 Menu 的頁：
//   有 read   → 監控總覽 TSMDashboard（一般物業/警衛）
//   只有 audit → 門禁事件查詢 TSMEventLog（稽核型帳號）
//   設定型權限 → 主機設定 TSMSettings（write/device/r3/admin/breakglass，guard 為全集任一即可進）
const accessControlEntry = () => {
  const userFunctions = store.getters.getSelectCommunity?.Function || [];
  const has = (suffix) => userFunctions.includes(`community.sa.tsm.${suffix}`);
  if (has("read")) return { name: "TSMDashboard" };
  if (has("audit")) return { name: "TSMEventLog" };
  if (has("write") || has("device") || has("r3") || has("admin") || has("breakglass")) {
    return { name: "TSMSettings" };
  }
  return { name: "Menu" };
};

const haIntercomEntry = () => {
  const userFunctions = store.getters.getSelectCommunity?.Function || [];
  const has = (permissionName) => userFunctions.includes(permissionName);
  if (has("ha.intercom.device.read") || has("ha.intercom.device.manage") || has("ha.intercom.registration.read")) return { name: "HADeviceList" };
  if (has("ha.intercom.alarm.read") || has("ha.intercom.alarm.handle")) return { name: "HAAlarmList" };
  if (has("ha.intercom.diagnostics.read") || has("ha.intercom.elevator.call")) return { name: "HACommandLogList" };
  if (has("ha.intercom.message.read") || has("ha.intercom.message.send")) return { name: "HAIndoorMessageList" };
  if (has("ha.intercom.sip.read") || has("ha.intercom.sip.manage")) return { name: "HASIPEndpointList" };
  if (has("ha.intercom.notification.manage")) return { name: "HASettings" };
  return { name: "Menu" };
};

const routes = [
  {
    path: "/auth",
    name: "Auth",
    component: () => import(/* webpackChunkName: "login" */ "@/views/Login/LoginMainScreen.vue"),
    redirect: { name: "AccountLogin" },
    children: [
      {
        path: "login",
        name: "Login",
        redirect: { name: "AccountLogin" },
        component: () => import(/* webpackChunkName: "login" */ "@/views/Login/LoginView.vue"),
        children: [
          {
            path: "account",
            name: "AccountLogin",
            component: () => import(/* webpackChunkName: "login" */ "@/views/Login/AccountLogin.vue"),
            children: [],
          },
          {
            path: "qrcore",
            name: "QRCodeLogin",
            component: () => import(/* webpackChunkName: "login" */ "@/views/Login/QRCodeLogin.vue"),
            children: [],
          },
          {
            path: "scan",
            name: "ScanLogin",
            component: () => import(/* webpackChunkName: "login" */ "@/views/Login/ScanLogin.vue"),
            children: [],
          },
        ],
      },
      {
        path: "resetpassword",
        name: "ResetPassword",
        component: () => import(/* webpackChunkName: "main" */ "@/views/Login/ResetPasswordView.vue"),
        children: [],
      },
    ],
  },
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: () => import(/* webpackChunkName: "privacy" */ "@/views/PrivacyPolicy.vue"),
    meta: {
      auth: false,
    },
    children: [],
  },
  {
    path: "/app_privacy",
    name: "AppPrivacyPolicy",
    component: () => import(/* webpackChunkName: "privacy" */ "@/views/AppPrivacyPolicy.vue"),
    meta: {
      auth: false,
    },
    children: [],
  },
  {
    path: "/app_terms",
    name: "AppTermsOfService",
    component: () => import(/* webpackChunkName: "terms" */ "@/views/AppTermsOfService.vue"),
    meta: {
      auth: false,
    },
    children: [],
  },
  {
    path: "/shop_app_privacy",
    name: "ShopAppPrivacyPolicy",
    component: () => import(/* webpackChunkName: "shop-privacy" */ "@/views/ShopAppPrivacyPolicy.vue"),
    meta: {
      auth: false,
    },
    children: [],
  },
  {
    path: "/shop_app_terms",
    name: "ShopAppTermsOfService",
    component: () => import(/* webpackChunkName: "shop-terms" */ "@/views/ShopAppTermsOfService.vue"),
    meta: {
      auth: false,
    },
    children: [],
  },
  {
    path: "/datasafe",
    name: "DataSafe",
    component: () => import(/* webpackChunkName: "datasafe" */ "@/views/DataSafe.vue"),
    children: [],
  },
  // {
  //   path: "/pay/sinopac/mgmt_fee/:communityID/:orderID",
  //   // name: "PaySinopac",
  //   component: () => import(/* webpackChunkName: "pay-sinopac" */ "@/views/Pay/Sinopac/MgmtFeeReturnView.vue"),
  //   children: [],
  //   props: true,
  // },
  {
    path: "/",
    name: "MainScreen",
    component: () => import(/* webpackChunkName: "main" */ "@/views/MainScreen.vue"),
    redirect: { name: "Menu" },
    children: [
      {
        path: "menu",
        name: "Menu",
        component: () => import(/* webpackChunkName: "main" */ "@/views/MenuView.vue"),
        meta: {
          auth: true,
        },
        children: [],
      },
      {
        path: "postal",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.mail",
        },
        children: [
          {
            path: "despatcher",
            name: "PostalDespatcher",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/PostalDespatcher.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "receive",
            name: "PostalReceive",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/PostalReceive.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "list",
            name: "PostalList",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/PostalList.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            props: true,
            children: [],
          },
          {
            path: "return",
            name: "PostalReturn",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/PostalReturn.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "place",
            name: "PostalPlace",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/PostalPlace.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "type",
            name: "PackageType",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Package/PackageType.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "shipping_from",
            name: "ShippingFrom",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/ShippingFrom.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
          {
            path: "shipping_provider",
            name: "ShippingProvider",
            component: () => import(/* webpackChunkName: "postal" */ "@/views/Postal/ShippingProvider.vue"),
            meta: {
              auth: true,
              function: "community.mail",
            },
            children: [],
          },
        ],
      },
      {
        path: "guest",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.guest",
        },
        children: [
          {
            path: "check",
            name: "GuestCheck",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/GuestCheck.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
          {
            path: "borrowitem",
            name: "GuestBorrowItem",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/GuestBorrowItem.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
          {
            path: "visitreason",
            name: "VisitReason",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/VisitReason.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
          {
            path: "visitplace",
            name: "GuestVisitPlace",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/GuestVisitPlace.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
          {
            path: "leave",
            name: "GuestLeave",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/GuestLeave.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
          {
            path: "history",
            name: "GuestHistory",
            component: () => import(/* webpackChunkName: "guest" */ "@/views/Guest/GuestHistory.vue"),
            meta: {
              auth: true,
              function: "community.guest",
            },
            children: [],
          },
        ],
      },
      {
        path: "householdactive",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.household",
        },
        children: [
          {
            path: "menu",
            name: "HouseHoldActiveList",
            component: () => import(/* webpackChunkName: "householdactive" */ "@/views/HouseHoldActive/MainScreen.vue"),
            meta: {
              auth: true,
              function: "community.household",
            },
            children: [],
          },
        ],
      },
      {
        path: "householddetail",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.household",
        },
        children: [
          {
            path: "view",
            name: "HouseHoldDetail",
            component: () => import(/* webpackChunkName: "householddetail" */ "@/views/HouseHoldActive/HouseHoldDetail.vue"),
            meta: {
              auth: true,
              function: "community.household",
            },
            props: true,
            children: [],
          },
        ],
      },
      {
        path: "notification",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.notification",
        },
        children: [
          {
            path: "menu",
            name: "NotificationMainScreen",
            component: () => import(/* webpackChunkName: "notification" */ "@/views/Notification/NotificationMainScreen.vue"),
            meta: {
              auth: true,
              function: "community.notification",
            },
            children: [],
          },
          {
            path: "list",
            name: "NotificationList",
            component: () => import(/* webpackChunkName: "notification" */ "@/views/Notification/NotificationList.vue"),
            meta: {
              auth: true,
              function: "community.notification",
            },
            children: [],
          },
        ],
      },
      {
        path: "deposit",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.deposit",
        },
        children: [
          {
            path: "check",
            name: "DepositCheck",
            component: () => import(/* webpackChunkName: "deposit" */ "@/views/Deposit/DepositCheck.vue"),
            meta: {
              auth: true,
              function: "community.deposit",
            },
            children: [],
          },
          {
            path: "list",
            name: "DepositList",
            component: () => import(/* webpackChunkName: "deposit" */ "@/views/Deposit/DepositList.vue"),
            meta: {
              auth: true,
              function: "community.deposit",
            },
            children: [],
          },
          {
            path: "place",
            name: "DepositPlace",
            component: () => import(/* webpackChunkName: "deposit" */ "@/views/Deposit/DepositPlace.vue"),
            meta: {
              auth: true,
              function: "community.deposit",
            },
            children: [],
          },
          {
            path: "type",
            name: "DepositType",
            component: () => import(/* webpackChunkName: "deposit" */ "@/views/Deposit/DepositType.vue"),
            meta: {
              auth: true,
              function: "community.deposit",
            },
            children: [],
          },
        ],
      },
      {
        path: "point",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.point",
        },
        children: [
          {
            path: "manage",
            name: "PointManage",
            component: () => import(/* webpackChunkName: "point" */ "@/views/Point/PointManage.vue"),
            meta: {
              auth: true,
              function: "community.point",
            },
            children: [],
          },
          {
            path: "history",
            name: "PointHistory",
            component: () => import(/* webpackChunkName: "point" */ "@/views/Point/PointHistory.vue"),
            meta: {
              auth: true,
              function: "community.point",
            },
            children: [],
          },
          {
            path: "list",
            name: "PointList",
            component: () => import(/* webpackChunkName: "point" */ "@/views/Point/PointList.vue"),
            meta: {
              auth: true,
              function: "community.point",
            },
            children: [],
          },
          {
            path: "itemmanage",
            name: "PointItemManage",
            component: () => import(/* webpackChunkName: "point" */ "@/views/Point/PointItemManage.vue"),
            meta: {
              auth: true,
              function: "community.point",
            },
            children: [],
          },
        ],
      },
      {
        path: "facility",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.booking",
        },
        children: [
          {
            path: "list",
            name: "FacilityList",
            component: () => import(/* webpackChunkName: "facility" */ "@/views/Facility/FacilityList.vue"),
            meta: {
              auth: true,
              function: "community.booking",
            },
            children: [],
          },
          {
            path: "edit",
            name: "FacilityEdit",
            component: () => import(/* webpackChunkName: "facility" */ "@/views/Facility/FacilityEdit.vue"),
            meta: {
              auth: true,
              function: "community.booking",
            },
            props: true,
            children: [],
          },
          {
            path: "bookinglist",
            name: "FacilityBookingList",
            component: () => import(/* webpackChunkName: "facility" */ "@/views/Facility/FacilityBookingList.vue"),
            meta: {
              auth: true,
              function: "community.booking",
            },
            children: [],
          },
          {
            path: "borrowitem",
            name: "FacilityBorrowItem",
            component: () => import(/* webpackChunkName: "facility" */ "@/views/Facility/FacilityBorrowItem.vue"),
            meta: {
              auth: true,
              function: "community.booking",
            },
            children: [],
          },
          {
            path: "type",
            name: "FacilityType",
            component: () => import(/* webpackChunkName: "facility" */ "@/views/Facility/FacilityType.vue"),
            meta: {
              auth: true,
              function: "community.booking",
            },
            children: [],
          },
        ],
      },
      {
        path: "news",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.announcement",
        },
        children: [
          {
            path: "list",
            name: "NewsList",
            component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsList.vue"),
            meta: {
              auth: true,
              function: "community.announcement",
            },
            children: [],
          },
          {
            path: "edit",
            name: "NewsEdit",
            component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsEdit.vue"),
            meta: {
              auth: true,
              function: "community.announcement",
            },
            props: true,
            children: [],
          },
          {
            path: "view",
            name: "NewsView",
            component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsView.vue"),
            meta: {
              auth: true,
              function: "community.announcement",
            },
            props: true,
            children: [],
          },
          {
            path: "type",
            name: "NewsType",
            component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsType.vue"),
            meta: {
              auth: true,
              function: "community.announcement",
            },
            children: [],
          },
        ],
      },
      {
        path: "feedback",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.feedback",
        },
        children: [
          {
            path: "list",
            name: "FeedbackList",
            component: () => import(/* webpackChunkName: "feedback" */ "@/views/Feedback/FeedbackList.vue"),
            meta: {
              auth: true,
              function: "community.feedback",
            },
            children: [],
          },
          {
            path: "type",
            name: "FeedbackType",
            component: () => import(/* webpackChunkName: "feedback" */ "@/views/Feedback/FeedbackType.vue"),
            meta: {
              auth: true,
              function: "community.feedback",
            },
            children: [],
          },
          {
            path: "reply",
            name: "FeedbackReply",
            component: () => import(/* webpackChunkName: "feedback" */ "@/views/Feedback/FeedbackReply.vue"),
            meta: {
              auth: true,
              function: "community.feedback",
            },
            props: true,
            children: [],
          },
        ],
      },
      {
        path: "rules",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.statute",
        },
        children: [
          {
            path: "list",
            name: "RulesList",
            component: () => import(/* webpackChunkName: "rules" */ "@/views/Rules/RulesList.vue"),
            meta: {
              auth: true,
              function: "community.statute",
            },
            children: [],
          },
          {
            path: "edit",
            name: "RulesEdit",
            component: () => import(/* webpackChunkName: "rules" */ "@/views/Rules/RulesEdit.vue"),
            meta: {
              auth: true,
              function: "community.statute",
            },
            props: true,
            children: [],
          },
          {
            path: "view",
            name: "RulesView",
            component: () => import(/* webpackChunkName: "rules" */ "@/views/Rules/RulesView.vue"),
            meta: {
              auth: true,
              function: "community.statute",
            },
            props: true,
            children: [],
          },
          {
            path: "type",
            name: "RulesType",
            component: () => import(/* webpackChunkName: "news" */ "@/views/Rules/RulesType.vue"),
            meta: {
              auth: true,
              function: "community.statute",
            },
            children: [],
          },
        ],
      },
      {
        path: "vote",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.vote",
        },
        children: [
          {
            path: "list",
            name: "VoteList",
            component: () => import(/* webpackChunkName: "vote" */ "@/views/Vote/VoteList.vue"),
            meta: {
              auth: true,
              function: "community.vote",
            },
            children: [],
          },
          {
            path: "edit",
            name: "VoteEdit",
            component: () => import(/* webpackChunkName: "vote" */ "@/views/Vote/VoteEdit.vue"),
            meta: {
              auth: true,
              function: "community.vote",
            },
            props: true,
            children: [],
          },
          {
            path: "detail",
            name: "VoteDetail",
            component: () => import(/* webpackChunkName: "vote" */ "@/views/Vote/VoteDetail.vue"),
            meta: {
              auth: true,
              function: "community.vote",
            },
            props: true,
            children: [],
          },
        ],
      },
      {
        path: "gas",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.gas",
        },
        children: [
          {
            path: "list",
            name: "GasList",
            component: () => import(/* webpackChunkName: "gas" */ "@/views/Gas/GasList.vue"),
            meta: {
              auth: true,
              function: "community.gas",
            },
            children: [],
          },
          {
            path: "detail",
            name: "GasDetail",
            component: () => import(/* webpackChunkName: "gas" */ "@/views/Gas/GasDetail.vue"),
            meta: {
              auth: true,
              function: "community.gas",
            },
            props: true,
            children: [],
          },
        ],
      },
      {
        path: "calendar",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.calendar",
        },
        children: [
          {
            path: "view",
            name: "CalendarView",
            component: () => import(/* webpackChunkName: "calendar" */ "@/views/Calendar/CalendarView.vue"),
            meta: {
              auth: true,
              function: "community.calendar",
            },
            children: [],
          },
        ],
      },
      {
        path: "mgmtfee",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.managementfee",
        },
        children: [
          {
            path: "list",
            name: "MgmtFeeList",
            component: () => import(/* webpackChunkName: "mgmtfee" */ "@/views/MgmtFee/MgmtFeeList.vue"),
            meta: {
              auth: true,
              module: "module.managementFee",
              function: "community.managementfee",
            },
            children: [],
          },
          {
            path: "setting",
            name: "MgmtFeeSetting",
            component: () => import(/* webpackChunkName: "mgmtfee" */ "@/views/MgmtFee/MgmtFeeSetting.vue"),
            meta: {
              auth: true,
              module: "module.managementFee",
              function: "community.managementfee",
            },
            props: true,
            children: [],
          },
          {
            path: "payinfo",
            name: "MgmtFeePayInfo",
            component: () => import(/* webpackChunkName: "mgmtfee" */ "@/views/MgmtFee/MgmtFeePayInfo.vue"),
            meta: {
              auth: true,
              module: "module.managementFee",
              function: "community.managementfee",
            },
            props: true,
            children: [],
          },
          {
            path: "item",
            name: "MgmtFeeSettingItem",
            component: () => import(/* webpackChunkName: "mgmtfee" */ "@/views/MgmtFee/MgmtFeeItem.vue"),
            meta: {
              auth: true,
              module: "module.managementFee",
              function: "community.managementfee",
            },
            props: true,
            children: [],
          },
        ],
      },
      {
        path: "cctv",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.cctv",
          module: "module.cctv",
        },
        children: [
          {
            path: "list",
            name: "CCTVList",
            component: () => import(/* webpackChunkName: "cctv" */ "@/views/CCTV/CCTVList.vue"),
            meta: {
              auth: true,
              function: "community.cctv",
              module: "module.cctv",
            },
            children: [],
          },
          {
            path: "cms",
            name: "CCTVCMS",
            component: () => import(/* webpackChunkName: "cctv" */ "@/views/CCTV/CMS.vue"),
            meta: {
              auth: true,
              function: "community.cctv",
              module: "module.cctv",
            },
            children: [],
          },
        ],
      },
      {
        path: "charge",
        component: Empty,
        // component: () => import(/* webpackChunkName: "charge" */ "@/views/Charge/ChargeMenu.vue"),
        redirect: { name: "ChargeDashboard" },
        meta: {
          auth: true,
          function: "community.charge",
          module: "module.charge",
        },
        children: [
          {
            path: "dashboard",
            name: "ChargeDashboard",
            component: () => import(/* webpackChunkName: "charge" */ "@/views/Charge/ChargeDashboard.vue"),
            meta: {
              auth: true,
              function: "community.charge",
              module: "module.charge",
            },
            children: [],
          },
          {
            path: "total",
            name: "ChargeTotal",
            component: () => import(/* webpackChunkName: "charge" */ "@/views/Charge/ChargeDashboard.vue"),
            meta: {
              auth: true,
              function: "community.charge",
              module: "module.charge",
            },
            children: [],
          },
          {
            path: "device",
            name: "ChargeDevice",
            component: () => import(/* webpackChunkName: "charge" */ "@/views/Charge/ChargeDashboard.vue"),
            meta: {
              auth: true,
              function: "community.charge",
              module: "module.charge",
            },
            children: [],
          },
          {
            path: "setting",
            name: "ChargeSetting",
            component: () => import(/* webpackChunkName: "charge" */ "@/views/Charge/ChargeDashboard.vue"),
            meta: {
              auth: true,
              function: "community.charge",
              module: "module.charge",
            },
            children: [],
          },
        ],
      },
      {
        path: "smart-box",
        component: Empty,
        redirect: { name: "SmartBoxDevices" },
        meta: {
          auth: true,
          module: "module.smartBox",
        },
        children: [
          {
            path: "main",
            name: "SmartBoxMain",
            redirect: { name: "SmartBoxDevices" },
            component: () => import(/* webpackChunkName: "smartbox" */ "@/views/SmartBox/SmartBoxLayout.vue"),
            meta: {
              auth: true,
              module: "module.smartBox",
            },
            children: [
              {
                path: "devices",
                name: "SmartBoxDevices",
                component: () => import(/* webpackChunkName: "smartbox" */ "@/views/SmartBox/SmartBoxDevices.vue"),
                meta: { auth: true, module: "module.smartBox" },
                children: [],
              },
              {
                path: "zones",
                name: "SmartBoxZones",
                component: () => import(/* webpackChunkName: "smartbox" */ "@/views/SmartBox/SmartBoxZones.vue"),
                meta: { auth: true, module: "module.smartBox" },
                children: [],
              },
              {
                path: "presets",
                name: "SmartBoxPresets",
                component: () => import(/* webpackChunkName: "smartbox" */ "@/views/SmartBox/SmartBoxPresets.vue"),
                meta: { auth: true, module: "module.smartBox" },
                children: [],
              },
              {
                path: "logs",
                name: "SmartBoxLogs",
                component: () => import(/* webpackChunkName: "smartbox" */ "@/views/SmartBox/SmartBoxLogs.vue"),
                meta: { auth: true, module: "module.smartBox" },
                children: [],
              },
            ],
          },
        ],
      },
      {
        path: "cloud-intercom",
        component: Empty,
        redirect: { name: "CloudIntercomStationList" },
        meta: {
          auth: true,
          module: "module.cloudIntercom",
          functions: cloudIntercomReadFunctions,
        },
        children: [
          {
            path: "list",
            name: "CloudIntercomList",
            redirect: { name: "CloudIntercomStationList" },
            component: () => import(/* webpackChunkName: "cloud-intercom" */ "@/views/Intercom/IntercomList.vue"),
            meta: {
              auth: true,
              module: "module.cloudIntercom",
              functions: cloudIntercomReadFunctions,
            },
            children: [
              {
                path: "stations",
                name: "CloudIntercomStationList",
                component: () => import(/* webpackChunkName: "cloud-intercom" */ "@/views/Intercom/IntercomStationList.vue"),
                meta: {
                  auth: true,
                  module: "module.cloudIntercom",
                  functions: cloudIntercomReadFunctions,
                },
                children: [],
              },
              {
                path: "households",
                name: "CloudIntercomHouseHoldList",
                component: () => import(/* webpackChunkName: "cloud-intercom" */ "@/views/Intercom/IntercomHouseHoldList.vue"),
                meta: {
                  auth: true,
                  module: "module.cloudIntercom",
                  functions: cloudIntercomReadFunctions,
                },
                children: [],
              },
              {
                path: "settings",
                name: "CloudIntercomSettings",
                component: () => import(/* webpackChunkName: "cloud-intercom" */ "@/views/Intercom/CloudIntercomSettings.vue"),
                meta: {
                  auth: true,
                  module: "module.cloudIntercom",
                  functions: cloudIntercomReadFunctions,
                },
                children: [],
              },
            ],
          },
        ],
      },
      {
        path: "ha",
        component: Empty,
        redirect: haIntercomEntry,
        meta: {
          auth: true,
          module: "module.ha",
          functions: haIntercomFunctions,
        },
        children: [
          {
            path: "list",
            name: "HAList",
            redirect: haIntercomEntry,
            component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HAList.vue"),
            meta: {
              auth: true,
              module: "module.ha",
              functions: haIntercomFunctions,
            },
            children: [
              {
                path: "device",
                name: "HADeviceList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HADeviceList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.device.read", "ha.intercom.device.manage", "ha.intercom.registration.read", "ha.intercom.registration.manage"],
                },
                children: [],
              },
              {
                path: "stations",
                name: "HAStationList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HAStationList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.device.read", "ha.intercom.device.manage"],
                },
                children: [],
              },
              {
                path: "alarm",
                name: "HAAlarmList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HAAlarmList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.alarm.read", "ha.intercom.alarm.handle"],
                },
                children: [],
              },
              {
                path: "command-log",
                name: "HACommandLogList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HACommandLogList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.diagnostics.read", "ha.intercom.elevator.call"],
                },
                children: [],
              },
              {
                path: "messages",
                name: "HAIndoorMessageList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HAIndoorMessageList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.message.read", "ha.intercom.message.send"],
                },
                children: [],
              },
              {
                path: "sip-endpoints",
                name: "HASIPEndpointList",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HASIPEndpointList.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.sip.read", "ha.intercom.sip.manage"],
                },
                children: [],
              },
              {
                path: "sip-diagnostics",
                name: "HASIPDiagnostics",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HASIPDiagnostics.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: ["ha.intercom.diagnostics.read"],
                },
                children: [],
              },
              {
                path: "settings",
                name: "HASettings",
                component: () => import(/* webpackChunkName: "ha" */ "@/views/HA/HASettings.vue"),
                meta: {
                  auth: true,
                  module: "module.ha",
                  functions: haSettingsFunctions,
                },
                children: [],
              },
            ],
          },
        ],
      },
      {
        path: "account",
        component: Empty,
        redirect: { name: "Menu" },
        meta: {
          auth: true,
          function: "community.account",
        },
        children: [
          {
            path: "manage",
            name: "AccountMainScreen",
            redirect: { name: "AccountList" },
            component: () => import(/* webpackChunkName: "account" */ "@/views/Account/AccountMainScreen.vue"),
            meta: {
              auth: true,
              function: "community.account",
            },
            children: [
              {
                path: "list",
                name: "AccountList",
                component: () => import(/* webpackChunkName: "account" */ "@/views/Account/AccountList.vue"),
                meta: {
                  auth: true,
                  function: "community.account",
                },
                children: [],
              },
              {
                path: "role_list",
                name: "AccountRoleList",
                component: () => import(/* webpackChunkName: "account" */ "@/views/Account/AccountRoleList.vue"),
                meta: {
                  auth: true,
                  function: "community.account",
                },
                children: [],
              },
            ],
          },
          // {
          //   path: "detail",
          //   name: "GasDetail",
          //   component: () => import(/* webpackChunkName: "account" */ "@/views/Gas/GasDetail.vue"),
          //   meta: {
          //     auth: true,
          //   },
          //   props: true,
          //   children: [],
          // },
        ],
      },
      {
        path: "nearbyshop",
        component: Empty,
        redirect: { name: "NearbyShopList" },
        meta: {
          auth: true,
          function: "community.surroundingstore",
        },
        children: [
          {
            path: "list",
            name: "NearbyShopList",
            component: () => import(/* webpackChunkName: "nearbyshop" */ "@/views/NearbyShop/ShopList.vue"),
            meta: {
              auth: true,
              function: "community.surroundingstore",
            },
            children: [],
          },
          {
            path: "manage",
            name: "NearbyShopManage",
            component: () => import(/* webpackChunkName: "nearbyshop" */ "@/views/NearbyShop/ShopManage.vue"),
            meta: {
              auth: true,
              function: "community.surroundingstore",
            },
            children: [],
          },
          {
            path: "blacklist",
            name: "NearbyShopBlacklist",
            component: () => import(/* webpackChunkName: "nearbyshop" */ "@/views/NearbyShop/ShopBlacklist.vue"),
            meta: {
              auth: true,
              function: "community.surroundingstore",
            },
            children: [],
          },
          {
            path: "calls",
            name: "NearbyShopCalls",
            component: () => import(/* webpackChunkName: "nearbyshop" */ "@/views/NearbyShop/ShopCallRecords.vue"),
            meta: {
              auth: true,
              function: "community.surroundingstore",
            },
            children: [],
          },
        ],
      },
      {
        path: "yongjiajie-elevator",
        name: "YongjiajieElevator",
        component: () => import(/* webpackChunkName: "yongjiajie-elevator" */ "@/views/Elevator/ElevatorManagement.vue"),
        meta: {
          auth: true,
          module: "module.yongjiajieElevator",
          functions: ["community.elevator.read", "community.elevator.manage", "community.elevator.control"],
        },
        children: [],
      },
    ],
  },
  {
    path: "/fm",
    component: () => import(/* webpackChunkName: "fm_main" */ "@/views/FM/MainScreen.vue"),
    meta: {
      auth: true,
      function: "community.asset",
      module: "module.asset",
    },
    redirect: { name: "BuildingData" },
    children: [
      {
        path: "dashboard",
        name: "FMMainScreen",
        component: () => import(/* webpackChunkName: "fm_main" */ "@/views/FM/FMDashboard.vue"),
        meta: {
          auth: true,
          function: "community.asset",
          module: "module.asset",
        },
        children: [],
      },
      {
        path: "asset",
        component: Empty,
        // component: () => import(/* webpackChunkName: "asset" */ "@/views/Asset/AssetMenu.vue"),
        redirect: { name: "AssetOverview" },
        meta: {
          auth: true,
          function: "community.asset",
          module: "module.asset",
        },
        children: [
          {
            path: "overview",
            name: "AssetOverview",
            component: () => import(/* webpackChunkName: "asset" */ "@/views/FM/Asset/AssetOverview.vue"),
            meta: {
              auth: true,
              function: "community.asset",
              module: "module.asset",
            },
            children: [],
          },
          {
            path: "list",
            name: "AssetList",
            props: true,
            component: () => import(/* webpackChunkName: "asset" */ "@/views/FM/Asset/AssetList.vue"),
            meta: {
              auth: true,
              function: "community.asset",
              module: "module.asset",
            },
            children: [],
          },
          {
            path: "view",
            name: "AssetView",
            props: true,
            component: () => import(/* webpackChunkName: "asset" */ "@/views/FM/Asset/AssetView.vue"),
            meta: {
              auth: true,
              function: "community.asset",
              module: "module.asset",
            },
            children: [],
          },
          {
            path: "type_list",
            name: "AssetTypeList",
            component: () => import(/* webpackChunkName: "asset" */ "@/views/FM/Asset/AssetTypeList.vue"),
            meta: {
              auth: true,
              function: "community.asset",
              module: "module.asset",
            },
            children: [],
          },
          {
            path: "place_list",
            name: "AssetPlaceList",
            component: () => import(/* webpackChunkName: "asset" */ "@/views/FM/Asset/AssetPlaceList.vue"),
            meta: {
              auth: true,
              function: "community.asset",
              module: "module.asset",
            },
            children: [],
          },
        ],
      },
      {
        path: "storage",
        component: Empty,
        redirect: { name: "StorageList" },
        meta: {
          auth: true,
          function: "community.storage",
          module: "module.storage",
        },
        children: [
          {
            path: "list",
            name: "StorageList",
            props: true,
            component: () => import(/* webpackChunkName: "storage" */ "@/views/FM/Storage/StorageList.vue"),
            meta: {
              auth: true,
              function: "community.storage",
              module: "module.storage",
            },
            children: [],
          },
          {
            path: "history",
            name: "StorageChangeHistory",
            props: true,
            component: () => import(/* webpackChunkName: "storage" */ "@/views/FM/Storage/StorageChangeHistory.vue"),
            meta: {
              auth: true,
              function: "community.storage",
              module: "module.storage",
            },
            children: [],
          },
          {
            path: "type_list",
            name: "StorageTypeList",
            component: () => import(/* webpackChunkName: "storage" */ "@/views/FM/Storage/StorageTypeList.vue"),
            meta: {
              auth: true,
              function: "community.storage",
              module: "module.storage",
            },
            children: [],
          },
          {
            path: "place_list",
            name: "StoragePlaceList",
            component: () => import(/* webpackChunkName: "storage" */ "@/views/FM/Storage/StoragePlaceList.vue"),
            meta: {
              auth: true,
              function: "community.storage",
              module: "module.storage",
            },
            children: [],
          },
          {
            path: "unit_list",
            name: "StorageUnitList",
            component: () => import(/* webpackChunkName: "storage" */ "@/views/FM/Storage/StorageUnitList.vue"),
            meta: {
              auth: true,
              function: "community.storage",
              module: "module.storage",
            },
            children: [],
          },
        ],
      },
      {
        path: "building",
        component: Empty,
        redirect: { name: "BuildingData" },
        meta: {
          auth: true,
          function: "community.buildingManage",
          module: "module.buildingManage",
        },
        children: [
          {
            path: "data",
            name: "BuildingData",
            props: true,
            component: () => import(/* webpackChunkName: "building" */ "@/views/FM/Building/BuildingData.vue"),
            meta: {
              auth: true,
              function: "community.buildingManage",
              module: "module.buildingManage",
            },
            children: [],
          },
          {
            path: "household",
            name: "HouseHoldCertificateList",
            props: true,
            component: () => import(/* webpackChunkName: "building" */ "@/views/FM/Building/HouseHoldCertificateList.vue"),
            meta: {
              auth: true,
              function: "community.buildingManage",
              module: "module.buildingManage",
            },
            children: [],
          },
          {
            path: "rent",
            name: "HouseHoldRentData",
            props: true,
            component: () => import(/* webpackChunkName: "building" */ "@/views/FM/Building/HouseHoldRentData.vue"),
            meta: {
              auth: true,
              function: "community.buildingManage",
              module: "module.buildingManage",
            },
            children: [],
          },
          {
            path: "parking",
            name: "ParkingMgt",
            props: true,
            component: () => import(/* webpackChunkName: "building" */ "@/views/FM/Building/ParkingMgt.vue"),
            meta: {
              auth: true,
              function: "community.buildingManage",
              module: "module.buildingManage",
            },
            children: [],
          },
        ],
      },
      // TODO: 效能管理
      {
        path: "analyze",
        component: Empty,
        redirect: { name: "EquipmentOperationEfficiency" },
        meta: {
          auth: true,
          function: "community.analyze",
          module: "module.analyze",
        },
        children: [
          {
            path: "equipment_operation_efficiency",
            name: "EquipmentOperationEfficiency",
            component: () => import(/* webpackChunkName: "efficiency" */ "@/views/FM/Analyze/EquipmentOperationEfficiency.vue"),
            meta: {
              auth: true,
              function: "community.analyze",
              module: "module.analyze",
            },
            children: [],
          },
          {
            path: "equipment_life_cycle",
            name: "EquipmentLifeCycle",
            component: () => import(/* webpackChunkName: "efficiency" */ "@/views/FM/Analyze/EquipmentLifeCycle.vue"),
            meta: {
              auth: true,
              function: "community.analyze",
              module: "module.analyze",
            },
            children: [],
          },
          {
            path: "equipment_operation_analysis",
            name: "EquipmentOperationAnalysis",
            component: () => import(/* webpackChunkName: "efficiency" */ "@/views/FM/Analyze/EquipmentOperationAnalysis.vue"),
            meta: {
              auth: true,
              function: "community.analyze",
              module: "module.analyze",
            },
            children: [],
          },
          {
            path: "equipment_operation_control",
            name: "EquipmentOperationControl",
            component: () => import(/* webpackChunkName: "efficiency" */ "@/views/FM/Analyze/EquipmentOperationControl.vue"),
            meta: {
              auth: true,
              function: "community.analyze",
              module: "module.analyze",
            },
            children: [],
          },
          {
            path: "energy_consumption_data",
            name: "EnergyConsumptionData",
            component: () => import(/* webpackChunkName: "efficiency" */ "@/views/FM/Analyze/EnergyConsumptionData.vue"),
            meta: {
              auth: true,
              function: "community.analyze",
              module: "module.analyze",
            },
            children: [],
          },
        ],
      },
      {
        path: "organization",
        component: Empty,
        redirect: { name: "VendorList" },
        meta: {
          auth: true,
          function: "community.organization",
          module: "module.organization",
        },
        children: [
          {
            path: "vendor_list",
            name: "VendorList",
            component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/VendorList.vue"),
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [],
          },
          {
            path: "internal_people_list",
            name: "InternalPeopleList",
            component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/InternalPeopleList.vue"),
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [],
          },
          {
            path: "department_list",
            name: "DepartmentList",
            component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/DepartmentList.vue"),
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [],
          },
          {
            path: "commissioner_list",
            name: "CommissionerList",
            component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/CommissionerList.vue"),
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [],
          },
          {
            path: "supervision_record_list",
            name: "SupervisionRecordList",
            component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/SupervisionRecordList.vue"),
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [],
          },

          {
            path: "attendance",
            component: Empty,
            redirect: { name: "AttendanceDashboard" },
            meta: {
              auth: true,
              function: "community.organization",
              module: "module.organization",
            },
            children: [
              {
                path: "dashboard",
                name: "AttendanceDashboard",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/AttendanceDashboard.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "shiftCheck",
                name: "ShiftCheck",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/ShiftCheck.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "shift",
                name: "ShiftList",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/ShiftList.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "shift_type",
                name: "ShiftTypeList",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/ShiftTypeList.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "record",
                name: "AttendanceRecords",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/AttendanceRecords.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "approval",
                name: "AttendanceApproval",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/AttendanceApproval.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
              {
                path: "leave_type",
                name: "LeaveTypeList",
                component: () => import(/* webpackChunkName: "organization" */ "@/views/FM/Organization/Attendance/LeaveTypeList.vue"),
                meta: {
                  auth: true,
                  function: "community.organization",
                  module: "module.organization",
                },
                children: [],
              },
            ],
          },
        ],
      },
      {
        path: "maintenance",
        component: Empty,
        redirect: { name: "ReportList" },
        meta: {
          auth: true,
          function: "community.maintenance",
          module: "module.maintenance",
        },
        children: [
          {
            path: "report_list",
            name: "ReportList",
            component: () => import(/* webpackChunkName: "maintenance" */ "@/views/FM/Maintenance/ReportList.vue"),
            meta: {
              auth: true,
              function: "community.maintenance",
              module: "module.maintenance",
            },
            children: [],
          },
          {
            path: "work_order_list",
            name: "WorkOrderList",
            component: () => import(/* webpackChunkName: "maintenance" */ "@/views/FM/Maintenance/WorkOrderList.vue"),
            meta: {
              auth: true,
              function: "community.maintenance",
              module: "module.maintenance",
            },
            children: [],
          },
          {
            path: "contract_list",
            name: "ContractList",
            component: () => import(/* webpackChunkName: "maintenance" */ "@/views/FM/Maintenance/ContractList.vue"),
            meta: {
              auth: true,
              function: "community.maintenance",
              module: "module.maintenance",
            },
            children: [],
          },
          {
            path: "routine_list",
            name: "RoutineList",
            component: () => import(/* webpackChunkName: "maintenance" */ "@/views/FM/Maintenance/RoutineList.vue"),
            meta: {
              auth: true,
              function: "community.maintenance",
              module: "module.maintenance",
            },
            children: [],
          },
          {
            path: "maintenance_calendar",
            name: "MaintenanceCalendar",
            component: () => import(/* webpackChunkName: "maintenance" */ "@/views/FM/Maintenance/MaintenanceCalendar.vue"),
            meta: {
              auth: true,
              function: "community.maintenance",
              module: "module.maintenance",
            },
            children: [],
          },
        ],
      },
    ],
  },
  {
    path: "/ba",
    component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BALayout.vue"),
    redirect: { name: "BADashboard" },
    meta: {
      auth: true,
      function: "community.ba",
      module: "module.ba",
    },
    children: [
      {
        path: "dashboard",
        name: "BADashboard",
        component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BADashboard.vue"),
        meta: { auth: true, function: "community.ba", module: "module.ba" },
        children: [],
      },
      {
        path: "settings",
        name: "BASettings",
        component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BASettings.vue"),
        meta: { auth: true, function: "community.ba", module: "module.ba" },
        children: [],
      },
      {
        path: "types",
        name: "BADeviceTypes",
        component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BADeviceTypes.vue"),
        meta: { auth: true, function: "community.ba", module: "module.ba" },
        children: [],
      },
      {
        path: "devices",
        name: "BADevices",
        component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BADevices.vue"),
        meta: { auth: true, function: "community.ba", module: "module.ba" },
        children: [],
      },
      {
        path: "devices/:did",
        name: "BADeviceDetail",
        component: () => import(/* webpackChunkName: "ba" */ "@/views/BA/BADeviceDetail.vue"),
        meta: { auth: true, function: "community.ba", module: "module.ba" },
        props: true,
        children: [],
      },
    ],
  },
  {
    path: "/access",
    component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/AccessControlLayout.vue"),
    redirect: accessControlEntry,
    meta: {
      auth: true,
      functions: accessControlFunctions,
      module: "module.sa",
    },
    children: [
      {
        path: "dashboard",
        name: "TSMDashboard",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMDashboard.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "events",
        name: "TSMEventLog",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMEventLog.vue"),
        meta: { auth: true, function: "community.sa.tsm.audit", module: "module.sa" },
        children: [],
      },
      {
        path: "alerts",
        name: "TSMAlertPanel",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMAlertPanel.vue"),
        meta: { auth: true, function: "community.sa.tsm.audit", module: "module.sa" },
        children: [],
      },
      {
        path: "audit",
        name: "TSMAuditLog",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMAuditLog.vue"),
        meta: { auth: true, function: "community.sa.tsm.audit", module: "module.sa" },
        children: [],
      },
      {
        path: "operations",
        name: "TSMOperationConsole",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMOperationConsole.vue"),
        meta: { auth: true, function: "community.sa.tsm.r3", module: "module.sa" },
        children: [],
      },
      {
        path: "accounts",
        name: "TSMAccountManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMAccountManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "programs",
        name: "TSMProgramManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMProgramManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "devices",
        name: "TSMDeviceManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMDeviceManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "maps",
        name: "TSMMapSetting",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMMapSetting.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "door-groups",
        name: "TSMDoorGroupManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMDoorGroupManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "users",
        redirect: { name: "TSMCardUserManage" },
      },
      {
        path: "card-users",
        name: "TSMCardUserManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMCardUserManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "groups",
        name: "TSMGroupManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMGroupManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        // 電梯通行樓層：歸通行規則組⑤的獨立入口，共用 GroupManage 元件、預設開電梯權限分頁
        path: "elevator-floors",
        name: "TSMElevatorFloors",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMGroupManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "parking-groups",
        name: "TSMParkingGroupManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMParkingGroupManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "schedules",
        name: "TSMScheduleManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMScheduleManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "mailbox",
        name: "TSMMailboxManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMMailboxManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "weekly-schedules",
        redirect: { name: "TSMWeeklySchedule" },
      },
      {
        path: "weekly-schedule",
        name: "TSMWeeklySchedule",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMWeeklySchedule.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "settings",
        name: "TSMSettings",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMSettings.vue"),
        meta: { auth: true, functions: accessControlFunctions, module: "module.sa" },
        children: [],
      },
      {
        path: "device-transfer",
        name: "TSMDeviceTransfer",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMDeviceTransfer.vue"),
        meta: { auth: true, functions: ["community.sa.tsm.device", "community.sa.tsm.r3"], module: "module.sa" },
        children: [],
      },
      {
        path: "danger",
        name: "TSMDangerZone",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/TSMDangerZone.vue"),
        meta: {
          auth: true,
          functions: ["community.sa.tsm.r3", "community.sa.tsm.breakglass", "community.sa.tsm.admin"],
          module: "module.sa",
        },
        children: [],
      },
      {
        path: "care-card",
        name: "CareCardManage",
        component: () => import(/* webpackChunkName: "access" */ "@/views/FM/AccessControl/CareCardManage.vue"),
        meta: { auth: true, function: "community.sa.tsm.read", module: "module.sa" },
        children: [],
      },
      {
        path: "linkage",
        redirect: { name: "CareCardManage", query: { tab: "templates" } },
      },
      {
        // 偵知連動/門禁整合已併入關懷卡範本流程; 保留舊路由轉址避免 404 (元件空殼已移除)
        path: "integration",
        redirect: { name: "CareCardManage", query: { tab: "templates" } },
      },
    ],
  },
  {
    path: "*",
    component: Empty,
    redirect: { name: "AccountLogin" },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

Vue.use(
  VueGtag,
  {
    config: { id: process.env.VUE_APP_GA_ID || "G-441ZJEY7H3" },
  },
  router,
);

// 新版部署後舊 chunk 不存在，自動重新載入
router.onError((error) => {
  if (/ChunkLoadError|Loading chunk|loading chunk/.test(error.message)) {
    window.location.reload();
  }
});

export default router;
